/**
 * Applying a freshly installed version means reloading the page, which would throw a player
 * out of a race or lose an edit in progress. This waits for a calm moment, shows a short
 * notice and reloads, unless the moment passes during the notice (a race starts, a dialog
 * opens): then the notice is withdrawn and the reload waits for the next calm moment.
 */
import { effect, type ReadonlySignal, untracked } from '@preact/signals';

export interface ReloadWhenSafeOptions {
  /** A new version is installed and waits to be loaded. */
  updateReady: ReadonlySignal<boolean>;
  /** Reloading now would lose nothing. */
  safe: ReadonlySignal<boolean>;
  /** Tells the player what is about to happen; returns a function that withdraws the notice. */
  announce(): () => void;
  reload(): void;
  /** Time to read the notice before the reload. */
  noticeMs: number;
}

/** Starts watching for a calm moment. Returns a function that stops watching. */
export function reloadWhenSafe(options: ReloadWhenSafeOptions): () => void {
  let pending: { timer: ReturnType<typeof setTimeout>; withdraw: () => void } | null = null;

  const cancel = () => {
    if (!pending) return;
    clearTimeout(pending.timer);
    pending.withdraw();
    pending = null;
  };

  const stopWatching = effect(() => {
    if (!options.updateReady.value || !options.safe.value) {
      cancel();
      return;
    }
    if (pending) return;
    untracked(() => {
      const withdraw = options.announce();
      const timer = setTimeout(() => {
        pending = null;
        options.reload();
      }, options.noticeMs);
      pending = { timer, withdraw };
    });
  });

  return () => {
    stopWatching();
    cancel();
  };
}
